import { useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { editCredit, ApiError } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';

interface Props {
  open: boolean;
  userId: string;
  firstName: string;
  currentMinutes: number;
  onClose: () => void;
  onSaved: () => void;
}

export function EditCreditDialog({
  open,
  userId,
  firstName,
  currentMinutes,
  onClose,
  onSaved,
}: Props) {
  const { push } = useToast();
  const [minutes, setMinutes] = useState('60');
  const [busy, setBusy] = useState(false);
  const minutesRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setMinutes('60');
      setBusy(false);
    }
  }, [open]);

  // Focus the amount field on open instead of using the autoFocus prop.
  useEffect(() => {
    if (open) {
      minutesRef.current?.focus();
    }
  }, [open]);

  const delta = Number(minutes);
  const valid = Number.isFinite(delta) && delta !== 0 && Math.abs(delta) <= 12000;
  // The API floors the balance at 0, so the projected balance never goes negative.
  const projected = Math.max(0, currentMinutes + (valid ? delta : 0));

  const handleSave = async () => {
    if (!valid) {
      push('error', 'Enter a non-zero amount between -12000 and 12000.');
      return;
    }
    setBusy(true);
    try {
      const res = await editCredit(userId, delta);
      const verb = delta > 0 ? 'Added' : 'Removed';
      push(
        'success',
        `${verb} ${Math.abs(delta)} min. New balance: ${res.new_balance_minutes} min.`,
      );
      onClose();
      onSaved();
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Edit credit for ${firstName}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ ...T.caption, margin: 0 }}>
          Current balance: <strong>{currentMinutes} min</strong>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={T.label}>Adjustment in minutes (negative removes)</span>
          <input
            ref={minutesRef}
            type="number"
            value={minutes}
            min={-12000}
            max={12000}
            step={30}
            onChange={(e) => setMinutes(e.target.value)}
            style={{
              padding: '8px 10px',
              border: `1px solid ${PS.border}`,
              borderRadius: 3,
              minHeight: 40,
              ...T.ui,
            }}
          />
        </label>
        <p style={{ ...T.caption, margin: 0 }}>
          {valid ? (
            <>
              New balance will be <strong>{projected} min</strong>.{' '}
            </>
          ) : null}
          Use a negative number to remove credit. The user is notified by Telegram if they have one linked.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => void handleSave()} disabled={busy || !valid}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
